import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { GuardianTeenBusinessListingApplicationResponse } from "../../../../types/guardianTypes";
import { formatBusinessListingCompensation, formatBusinessListingDuration, formatBusinessListingJobType } from "../../../../utils/BusinessListingUtils";
import { formatDateTime } from "../../../../utils/DateUtils";
import { formatCityStateZip } from "../../../../utils/LocationUtils";
import Button from "../../../ui/Button/Button";

type TeenProfileBusinessListingApplicationsSectionProps = {
  applications: GuardianTeenBusinessListingApplicationResponse[];
};

const applicationStatusLabel: Record<GuardianTeenBusinessListingApplicationResponse["applicationStatus"], string> = {
  APPLIED: "Applied",
  WITHDRAWN: "Withdrawn",
  REJECTED: "Rejected",
};

const listingStatusLabel: Record<NonNullable<GuardianTeenBusinessListingApplicationResponse["listingStatus"]>, string> = {
  DRAFT: "Draft",
  PUBLISHED: "Published",
  EXPIRED: "Expired",
  CLOSED: "Closed",
};

const TeenProfileBusinessListingApplicationsSection = ({
  applications,
}: TeenProfileBusinessListingApplicationsSectionProps) => {
  const [searchParams] = useSearchParams();
  const [expandedApplicationId, setExpandedApplicationId] = useState<string | null>(null);
  const highlightedApplicationId = searchParams.get("applicationId");

  useEffect(() => {
    if (!highlightedApplicationId) {
      return;
    }
    setExpandedApplicationId(highlightedApplicationId);
  }, [highlightedApplicationId]);

  useEffect(() => {
    if (!highlightedApplicationId) {
      return;
    }
    const highlightedCard = document.getElementById(`guardian-teen-business-application-${highlightedApplicationId}`);
    if (highlightedCard) {
      highlightedCard.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightedApplicationId, applications]);

  return (
    <section className="rounded-[2rem] border border-myteengig-border bg-white p-6 shadow-sm">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Business Listings</p>
        <h2 className="mt-2 text-2xl font-semibold tracking-tight text-myteengig-text-primary">Business listing applications</h2>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-myteengig-text-muted">
          Review business jobs this teen has applied to. These are separate from scheduled gigs and exist to help businesses connect with teens before any offline next steps.
        </p>
      </div>

      {applications.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-myteengig-border bg-myteengig-background px-5 py-6 text-sm text-myteengig-text-muted">
          No business listing applications yet.
        </div>
      ) : (
        <div className="mt-6 grid gap-4">
          {applications.map((application) => {
            const detailsAreOpen = expandedApplicationId === application.applicationId;
            const isHighlighted = application.applicationId === highlightedApplicationId;

            return (
              <article
                key={application.applicationId}
                id={`guardian-teen-business-application-${application.applicationId}`}
                className={`rounded-[1.5rem] border p-5 transition ${
                  isHighlighted
                    ? "border-myteengig-primary bg-[linear-gradient(180deg,_rgba(239,248,255,1),_rgba(255,255,255,1))] ring-2 ring-myteengig-primary/20"
                    : "border-myteengig-border bg-[linear-gradient(180deg,_rgba(255,255,255,1),_rgba(234,248,255,0.72))]"
                }`}
              >
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-3">
                          <h3 className="text-lg font-semibold text-myteengig-text-primary">
                            {application.listingTitle ?? "Unknown business listing"}
                          </h3>
                          <span className="rounded-full border border-myteengig-border bg-white px-3 py-1 text-xs font-semibold text-myteengig-text-primary">
                            {applicationStatusLabel[application.applicationStatus]}
                          </span>
                          {application.listingStatus ? (
                            <span className="rounded-full border border-myteengig-border bg-white px-3 py-1 text-xs font-semibold text-myteengig-text-muted">
                              {listingStatusLabel[application.listingStatus]}
                            </span>
                          ) : null}
                        </div>

                        <div className="mt-3 flex flex-wrap gap-3 text-sm text-myteengig-text-muted">
                          <span>{application.businessName ?? "Business"}</span>
                          <span>{application.category ?? "Category not set"}</span>
                          <span>{formatCityStateZip(application, "Location not set")}</span>
                          {application.payType && application.minPayAmountCents !== null && application.maxPayAmountCents !== null ? (
                            <span>
                              {formatBusinessListingCompensation(
                                application.payType,
                                application.minPayAmountCents,
                                application.maxPayAmountCents,
                                application.jobType,
                              )}
                            </span>
                          ) : null}
                        </div>

                        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-myteengig-text-muted">
                          <span>Applied {formatDateTime(application.createdAt)}</span>
                          {application.publishedAt ? <span>Posted {formatDateTime(application.publishedAt)}</span> : null}
                          {application.expiresAt ? <span>Expires {formatDateTime(application.expiresAt)}</span> : null}
                        </div>
                      </div>

                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setExpandedApplicationId((current) => current === application.applicationId ? null : application.applicationId)}
                        className="w-full sm:w-auto"
                      >
                        {detailsAreOpen ? "Hide Details" : "View Details"}
                      </Button>
                    </div>

                    {detailsAreOpen ? (
                      <div className="mt-4 grid gap-4">
                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                          <div className="rounded-2xl bg-white/80 px-4 py-3">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Application status</p>
                            <p className="mt-2 text-sm text-myteengig-text-primary">{applicationStatusLabel[application.applicationStatus]}</p>
                          </div>
                          <div className="rounded-2xl bg-white/80 px-4 py-3">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Job type</p>
                            <p className="mt-2 text-sm text-myteengig-text-primary">
                              {application.jobType ? formatBusinessListingJobType(application.jobType) : "Not set"}
                            </p>
                          </div>
                          <div className="rounded-2xl bg-white/80 px-4 py-3">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Usual shift</p>
                            <p className="mt-2 text-sm text-myteengig-text-primary">
                              {formatBusinessListingDuration(application.estimatedDurationMinutes) ?? "Not set"}
                            </p>
                          </div>
                        </div>

                        <div className="rounded-2xl bg-white/80 px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Listing description</p>
                          <p className="mt-2 text-sm leading-6 text-myteengig-text-primary">
                            {application.listingDescription ?? "No description provided."}
                          </p>
                        </div>

                        <div className="rounded-2xl bg-white/80 px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Teen message</p>
                          <p className="mt-2 text-sm leading-6 text-myteengig-text-primary">
                            {application.applicationMessage ?? "No message provided."}
                          </p>
                        </div>

                        {application.resume ? (
                          <div className="rounded-2xl bg-white/80 px-4 py-3">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Resume</p>
                            <a
                              href={application.resume.url}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-2 inline-flex text-sm font-medium text-myteengig-primary hover:underline"
                            >
                              {application.resume.originalFileName ?? "Download resume"}
                            </a>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>

                  <div className="xl:w-56">
                    <div className="rounded-[1.5rem] border border-myteengig-border bg-myteengig-background p-4 text-sm text-myteengig-text-muted">
                      <p>No guardian action is needed on business listing applications right now.</p>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default TeenProfileBusinessListingApplicationsSection;
